import React, { Fragment, useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "@1f/react-sdk";

//Vapor Components
import Stack from "@vapor/react-material/Stack";
import Box from "@vapor/react-material/Box";
import Modal from "@vapor/react-material/Modal";
import Divider from "@vapor/react-material/Divider";
import IconButton from "@vapor/react-material/IconButton";
import CircularProgress from "@vapor/react-material/CircularProgress";
import Tooltip from "@vapor/react-material/Tooltip";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";

//Icons
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import DownloadIcon from "@mui/icons-material/Download";

//Style
import { modalStylePdfViewer, chipGreen, chipYellow, chipRed, chipGray } from "../businessLogic/styles";
import VaporTag from "@vapor/react-custom/VaporTag";

const SCALE_STEP = 0.25;
const SCALE_MIN = 0.5;
const SCALE_MAX = 3.0;
const SCALE_DEFAULT = 1.0;

export const ModalPdfViewer = ({
    show,
    title,
    base64,
    documents = [],
    activeDocumentId = null,
    onSelectDocument,
    onCloseDocument,
    onClose,
    onDownload,
    signatureStatus = null,
}) => {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const renderTaskRef = useRef(null);
    const loadingTaskRef = useRef(null);

    const normalizedDocuments = documents.length > 0
        ? documents
        : base64
            ? [{ id: "single-document", title, base64, onDownload }]
            : [];
    const activeDocument = normalizedDocuments.find((document) => document.id === activeDocumentId)
        || normalizedDocuments[normalizedDocuments.length - 1]
        || null;
    const activeDocumentIndex = activeDocument
        ? normalizedDocuments.findIndex((document) => document.id === activeDocument.id)
        : -1;
    const currentDocumentId = activeDocument?.id || null;
    const currentTitle = activeDocument?.title || title;
    const currentBase64 = activeDocument?.base64 || base64;
    const currentDownload = activeDocument?.onDownload || onDownload;
    const currentSignatureStatus = activeDocument !== null && "signatureStatus" in (activeDocument || {})
        ? activeDocument.signatureStatus
        : signatureStatus;

    const [pdfDoc, setPdfDoc] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [scale, setScale] = useState(SCALE_DEFAULT);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const resetState = useCallback(() => {
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
            renderTaskRef.current = null;
        }
        if (loadingTaskRef.current) {
            loadingTaskRef.current.destroy?.();
            loadingTaskRef.current = null;
        }
        setPdfDoc((currentPdfDoc) => {
            currentPdfDoc?.destroy?.();
            return null;
        });
        setCurrentPage(1);
        setTotalPages(0);
        setScale(SCALE_DEFAULT);
        setIsLoading(false);
        setIsDownloading(false);
    }, []);

    // Load PDF when modal opens
    useEffect(() => {
        if (!show || !currentBase64) {
            resetState();
            return;
        }

        const loadPdf = async () => {
            let nextLoadingTask = null;

            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
            if (loadingTaskRef.current) {
                loadingTaskRef.current.destroy?.();
                loadingTaskRef.current = null;
            }

            setPdfDoc((currentPdfDoc) => {
                currentPdfDoc?.destroy?.();
                return null;
            });
            setCurrentPage(1);
            setTotalPages(0);
            setScale(SCALE_DEFAULT);
            setIsLoading(true);
            setIsDownloading(false);
            try {
                const pdfjsLib = window.pdfjsLib;
                if (!pdfjsLib) {
                    console.error("ModalPdfViewer: pdfjs-dist not available on window.pdfjsLib");
                    return;
                }

                let loadingTask;
                if (currentBase64) {
                    // Accepts both raw base64 and data URL (data:application/pdf;base64,...)
                    const raw = currentBase64.includes(",") ? currentBase64.split(",")[1] : currentBase64;
                    const binaryStr = atob(raw);
                    const bytes = new Uint8Array(binaryStr.length);
                    for (let i = 0; i < binaryStr.length; i++) {
                        bytes[i] = binaryStr.charCodeAt(i);
                    }
                    nextLoadingTask = pdfjsLib.getDocument({ data: bytes });
                } else {
                    console.error("ModalPdfViewer: no base64 provided");
                    return;
                }

                loadingTaskRef.current = nextLoadingTask;
                const doc = await nextLoadingTask.promise;
                if (loadingTaskRef.current !== nextLoadingTask) {
                    doc?.destroy?.();
                    return;
                }
                setPdfDoc(doc);
                setTotalPages(doc.numPages);
            } catch (e) {
                if (e?.name !== "RenderingCancelledException") {
                    console.error("ModalPdfViewer: failed to load PDF", e);
                }
            } finally {
                if (loadingTaskRef.current === nextLoadingTask) {
                    loadingTaskRef.current = null;
                }
                setIsLoading(false);
            }
        };

        loadPdf();
    }, [show, currentDocumentId, currentBase64, resetState]);

    // Render page whenever doc/page/scale changes
    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;

        const renderPage = async () => {
            // Cancel any in-progress render task
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }

            setIsLoading(true);
            try {
                const page = await pdfDoc.getPage(currentPage);
                const viewport = page.getViewport({ scale });
                const canvas = canvasRef.current;
                if (!canvas) return;
                const context = canvas.getContext("2d");
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                const renderTask = page.render({ canvasContext: context, viewport });
                renderTaskRef.current = renderTask;
                await renderTask.promise;
            } catch (e) {
                if (e?.name !== "RenderingCancelledException") {
                    console.error("ModalPdfViewer: render error", e);
                }
            } finally {
                setIsLoading(false);
                renderTaskRef.current = null;
            }
        };

        renderPage();
    }, [pdfDoc, currentPage, scale, currentDocumentId]);

    const handleClose = () => {
        resetState();
        onClose?.();
    };

    const handleSelectDocument = (documentId) => {
        if (!documentId || documentId === currentDocumentId) {
            return;
        }
        onSelectDocument?.(documentId);
    };

    const handleDocumentKeyDown = (event, documentId) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleSelectDocument(documentId);
        }
    };

    const handleCloseDocumentClick = (event, documentId) => {
        event.stopPropagation();
        event.preventDefault();
        onCloseDocument?.(documentId);
    };

    const handleFirstPage = () => setCurrentPage(1);
    const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
    const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
    const handleLastPage = () => setCurrentPage(totalPages);
    const handleZoomIn = () => setScale((s) => Math.min(SCALE_MAX, parseFloat((s + SCALE_STEP).toFixed(2))));
    const handleZoomOut = () => setScale((s) => Math.max(SCALE_MIN, parseFloat((s - SCALE_STEP).toFixed(2))));
    const handleZoomReset = () => setScale(SCALE_DEFAULT);
    const handleDownload = async () => {
        if (!currentDownload) return;
        setIsDownloading(true);
        try {
            await currentDownload();
        } catch (e) {
            console.error("ModalPdfViewer: download error", e);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Fragment>
            <Modal
                key="pdfViewerModal"
                aria-labelledby="pdf-viewer-title"
                open={show}
                onClose={handleClose}
            >
                <Box sx={modalStylePdfViewer}>
                    {/* Header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 3, py: 1.5, flexShrink: 0 }}
                    >
                        <ExtendedTypography
                            id="pdf-viewer-title"
                            variant="titleSmall"
                            color="primary.main"
                            noWrap
                            sx={{ maxWidth: "calc(100% - 48px)" }}
                        >
                            {currentTitle || t("component.pdfViewer.title", "Visualizzatore PDF")}
                        </ExtendedTypography>
                        <Tooltip title={t("actions.close", "Chiudi")}>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {normalizedDocuments.length > 1 && (
                        <>
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    display: "flex",
                                    gap: 1,
                                    overflowX: "auto",
                                    bgcolor: "#F6F7F9",
                                    flexShrink: 0,
                                }}
                            >
                                {normalizedDocuments.map((document, index) => {
                                    const isActive = document.id === currentDocumentId;
                                    const fallbackTitle = `${t("component.pdfViewer.title", "Visualizzatore PDF")} ${index + 1}`;

                                    return (
                                        <Stack
                                            key={document.id}
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleSelectDocument(document.id)}
                                            onKeyDown={(event) => handleDocumentKeyDown(event, document.id)}
                                            sx={{
                                                minWidth: 0,
                                                maxWidth: 220,
                                                px: 1.25,
                                                py: 0.75,
                                                borderRadius: "16px",
                                                border: "1px solid",
                                                borderColor: isActive ? "primary.main" : "divider",
                                                bgcolor: isActive ? "rgba(40, 128, 216, 0.08)" : "#FFFFFF",
                                                color: isActive ? "primary.main" : "text.primary",
                                                cursor: "pointer",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <ExtendedTypography
                                                variant="caption"
                                                noWrap
                                                sx={{
                                                    maxWidth: 150,
                                                    fontWeight: isActive ? 600 : 500,
                                                }}
                                            >
                                                {document.title || fallbackTitle}
                                            </ExtendedTypography>
                                            <Tooltip title={t("actions.close", "Chiudi")}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => handleCloseDocumentClick(event, document.id)}
                                                    sx={{ p: 0.25, color: "inherit" }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    );
                                })}
                            </Box>
                            <Divider orientation="horizontal" flexItem sx={{ flexShrink: 0 }} />
                        </>
                    )}

                    <Divider orientation="horizontal" flexItem sx={{ flexShrink: 0 }} />

                    {/* Canvas area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: "auto",
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            bgcolor: "#525659",
                            p: 2,
                            position: "relative",
                        }}
                    >
                        {isLoading && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    zIndex: 1,
                                }}
                            >
                                <CircularProgress color="inherit" sx={{ color: "#FFFFFF" }} />
                            </Box>
                        )}
                        <canvas
                            ref={canvasRef}
                            style={{
                                boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                                backgroundColor: "#FFFFFF",
                                display: totalPages === 0 ? "none" : "block",
                            }}
                        />
                    </Box>

                    <Divider orientation="horizontal" flexItem sx={{ flexShrink: 0 }} />

                    {/* Footer toolbar */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                        sx={{ px: 2, py: 1, flexShrink: 0 }}
                    >
                        {normalizedDocuments.length > 1 && (
                            <>
                                <ExtendedTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ minWidth: 50, textAlign: "center" }}
                                >
                                    {`${activeDocumentIndex + 1} / ${normalizedDocuments.length}`}
                                </ExtendedTypography>
                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                            </>
                        )}

                        {/* Page navigation */}
                        <Tooltip title={t("actions.firstPage", "Prima pagina")}>
                            <span>
                                <IconButton
                                    onClick={handleFirstPage}
                                    disabled={currentPage <= 1 || totalPages === 0}
                                    size="small"
                                >
                                    <FirstPageIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("actions.prevPage", "Pagina precedente")}>
                            <span>
                                <IconButton
                                    onClick={handlePrevPage}
                                    disabled={currentPage <= 1 || totalPages === 0}
                                    size="small"
                                >
                                    <ChevronLeftIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <ExtendedTypography variant="body2" sx={{ minWidth: 70, textAlign: "center" }}>
                            {totalPages > 0 ? `${currentPage} / ${totalPages}` : "—"}
                        </ExtendedTypography>
                        <Tooltip title={t("actions.nextPage", "Pagina successiva")}>
                            <span>
                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={currentPage >= totalPages || totalPages === 0}
                                    size="small"
                                >
                                    <ChevronRightIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("actions.lastPage", "Ultima pagina")}>
                            <span>
                                <IconButton
                                    onClick={handleLastPage}
                                    disabled={currentPage >= totalPages || totalPages === 0}
                                    size="small"
                                >
                                    <LastPageIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        {/* Zoom controls */}
                        <Tooltip title={t("actions.zoomOut", "Riduci zoom")}>
                            <span>
                                <IconButton
                                    onClick={handleZoomOut}
                                    disabled={scale <= SCALE_MIN || totalPages === 0}
                                    size="small"
                                >
                                    <ZoomOutIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("actions.zoomReset", "Zoom predefinito")}>
                            <span>
                                <IconButton
                                    onClick={handleZoomReset}
                                    disabled={totalPages === 0}
                                    size="small"
                                >
                                    <ZoomOutMapIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("actions.zoomIn", "Aumenta zoom")}>
                            <span>
                                <IconButton
                                    onClick={handleZoomIn}
                                    disabled={scale >= SCALE_MAX || totalPages === 0}
                                    size="small"
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <ExtendedTypography variant="caption" color="text.secondary" sx={{ minWidth: 50, textAlign: "center" }}>
                            {totalPages > 0 ? `${Math.round(scale * 100)}%` : ""}
                        </ExtendedTypography>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        {/* Signature status */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <ExtendedTypography variant="caption" color="text.secondary">
                                Stato firma:
                            </ExtendedTypography>
                            {currentSignatureStatus === "SIGNED" && <VaporTag variant="standard" label="Firmato" sx={chipGreen} />}
                            {currentSignatureStatus === "PENDING" && <VaporTag variant="standard" label="In attesa di firma" sx={chipYellow} />}
                            {currentSignatureStatus === "REJECTED" && <VaporTag variant="standard" label="Rifiutato" sx={chipRed} />}
                            {!currentSignatureStatus && <VaporTag variant="standard" label="Non firmato" sx={chipGray} />}
                        </Stack>

                        {currentDownload && (
                            <>
                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                <Tooltip title={t("actions.download", "Scarica")}>
                                    <span>
                                        <IconButton
                                            onClick={handleDownload}
                                            disabled={isDownloading || totalPages === 0}
                                            size="small"
                                        >
                                            {isDownloading
                                                ? <CircularProgress size={16} />
                                                : <DownloadIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                </Box>
            </Modal>
        </Fragment>
    );
};

export default ModalPdfViewer;
